import { Notification, Tile } from 'react-bulma-components';
import { note, transpose } from "@tonaljs/core";

import Abcjs from './abcjs';
import React from 'react';
import _ from 'lodash';
import { abcClefModifier } from '../lib/abcClef';
import { getKeyTransposedByInterval } from '../lib/transposeKey';
import { scientificToAbcNotation } from "@tonaljs/abc-notation";
import { transposeToInstrumentNoteKey } from '../lib/transposeNoteKey';
import { useGlobalState } from '../state';

// Trombone's (and other bass-clef instruments') practical lowest note is the E
// notated on the first ledger line below the staff (E2). If a note sequence
// dips below that after the bass-clef octave shift, the whole sequence is
// shifted back up an octave rather than left sitting under playable range.
const LOWEST_BASS_CLEF_MIDI = note("E2").midi;

const Notes = ({ children, ...props }) => {
  //console.log("t: "+props.t+", org_keytonic: "+props.org_keytonic+", i: "+props.i)
  
  const [currentInstrument] = useGlobalState('instrument');
  const clef = abcClefModifier(props.clef);
  const title = typeof props.title === "string" ? props.title : '';
  let defaultLevel = 4;
  // Bass clef (e.g. trombone, bass) sits exactly two octaves lower than treble
  // clef for the same staff position, so shift notes down accordingly — otherwise
  // they land in the same octave as treble-clef instruments and render far above
  // (and unplayably high for) the instrument's actual range.
  if (props.clef === 'f') {
    defaultLevel -= 2;
  }
  let keyTone = props.t;
  let org_keytonic = props.org_keytonic;
  let keyquality = props.q;

  // Check if transposeLevel is inherited from parent KeyWrapper
  if(props.transposeLevel)
  {
    defaultLevel+=props.transposeLevel;
  }
  // If Notes has a 'i'-property, change key end transposeLevel according to the interval
  if(props.i)
  {
    const { newKey, transposeLevel } = getKeyTransposedByInterval({ key: org_keytonic, transposeInterval: props.i });
    keyTone = newKey;
    defaultLevel = defaultLevel+transposeLevel;
  }

  if(["B","A"].some(k => keyTone.includes(k))) {
    defaultLevel = defaultLevel -1;
  }

  if(props.skipInstrumentTransponation!==true)
  {
   let newInst = transposeToInstrumentNoteKey(props.instrument, org_keytonic+defaultLevel.toString(), keyquality==='m'?false:true);
   keyTone = note(newInst).pc;
  }

  const noteLength = typeof props.l === "string" ? props.l : '';
  let defaultNoteLength = "1/8";
  if (["1/1","1/2","1/4","1/8","1/16"].some(l => noteLength.includes(l))) {
    defaultNoteLength = noteLength;
  }

  if(!children || children.length===0)
  {
    return <Notification color="white" style={{ padding:0,  marginTop: 10, marginBottom: 10, marginRight: 0, minWidth:200, maxWidth: 480 }}>
        <Abcjs
          abcNotation={`%%staffwidth 200\nT:${title}\nL:1/8\nK:C ${clef}\n`}
          parserParams={{}}
          engraverParams={{ responsive: 'resize' }}
          renderParams={{ viewportHorizontal: false }}
        /> 
      </Notification>
  }
  
  children = children.map((child) => {
    if(typeof child !== "string")
    {
      try{
        return child.props.children[0];
      }
      catch
      {
        console.log("Error reading children[0]: "+JSON.stringify(child.props.children));
      }
    }
    else{
      return child;
    }
  }).join(' ');

  let bars = children.split('|'); // Bars are separated by an '|'

  // Builds the abc-notation note body for a given base octave, also
  // returning every actual note's scientific pitch so the caller can check
  // whether the sequence fits the instrument's playable range.
  const buildAbcNotes = (level) => {
    let abcNotes = [];
    let pitches = [];
    bars.forEach((bar) => {
      bar = bar.replace(new RegExp('\r?\n','g'), ' x '); // Replace all newlines with ' x '
      let noteGroups = bar.split(','); // Quarter notes (and shorter) will be grouped (connected flags) automatically when rendered, an ',' will separate them
      let notesArr = noteGroups.map(group => _.compact(group.split(' ')));
      var tuneInArr = [];
      _.forEach(notesArr, (grp) =>
      {
        _.forEach(grp, (rawNote) =>
          {

            rawNote = rawNote.trim();
            // Find intervals i.e. '1P'
            // intervals with lengths i.e. '1P2', '1P/2'
            // intervals with added or subtracted octaves i.e. '-1P', '++1P'
            // rests i.e. 'z'
            // rests with length i.e. 'z2', 'z/2'

            const { sub, add, interval, nl, rest, length} = /^((?<sub>-*)(?<add>\+*)(?<interval>\d{1,2}[PmMdA]{1})|(?<nl>x)|(?<rest>z))(?<length>\/?\d)?/.exec(
              rawNote,
            )?.groups||{ sub: "", add:"", interval:"", rest:false, length:""};
            if(rest==="z"){
              tuneInArr.push({ alt:"", l: length||"", note: 'z'});
              return;
            }
            if(nl==="x"){
              tuneInArr.push({ nl: true }); // Insert newline intruction
              return;
            }
            let transposeLevel = level+add.length-sub.length;
            const tr = transpose(keyTone+transposeLevel.toString(), interval);
            pitches.push(tr);
            // Get the ABC notation, like: "C, C c c' _C ^C"
            let abcnote = scientificToAbcNotation(tr);
            const { alt, not } = /(?<alt>[_^]{1,2})?(?<not>[AaBbCcDdEeFfGg][',]*)/.exec(
              abcnote,
            )?.groups||{ alt: null, not: null};
            // Save a data object representing the note (including a property 'name' of the tone regardless of its alteration or octave)
            tuneInArr.push({ alt: alt||"", note: not, l: length||"" });
          }); // End forEach note in group
          // Insert a break-object after each group of notes so we later can render a 'space' in the abc-notation
          tuneInArr.push({ break: true});
      }); // End forEach group in bar

      // This forLoop will start at the last note in each bar and traverse to the left to find the next note with same 'name', if found it will adjust the current notes alteration.
      // It will then continue to the next note (from right to left) and search left for namelikes.
      for(let i=tuneInArr.length-1; i>0; i--)
      {
        let curr = tuneInArr[i];
        if(curr.note === "z" || curr.break === true)
        {
          // Current item is not an actual note
          continue;
        }

        let prev = null;
        // Search left for a note with the same name
        _.forEachRight(tuneInArr.slice(0,i), function(prevItem) {
          if(prevItem.note === curr.note)
          {
            prev = prevItem;
            return false; // A node with same name was found, return false to exit this forLoop
          }
        });
        // No name-matching note was found, start over with the next left note.
        if(prev === null)
        {
          continue;
        }

        // Modify current notes alteration based an the preceeding notes (with same name) alteration
        switch(curr.alt)
        {
          case "": // Current note isn't altered
            if(prev.alt!=="") // But the one before is
            {
              curr.alt = "="; // Reset the alteration for current note
            }
            break;
          case "^^":
          case "__":
          case "^":
          case "_":
            if(prev.alt===curr.alt) // The preceeding note is altered in the same way is current, remove current alteration
            {
              curr.alt="";
            }
            break;
        }
      }// End forEach bar

      // Combine all notes from all groups within this bar
      abcNotes = _.concat(abcNotes, _.map(tuneInArr, (item) => {
        if(item.break===true){
          return " "; // Convert breaks to 'space' in abc-notation
        }
        if(item.nl===true){
          return "\n"; // Convert newlines to '\n' in abc-notation
        }
        return `${item.alt}${item.note}${item.l}`; // Reconstruct abc-notation from datamodel
      }));

      abcNotes.push("|"); // Add a bar char
    });

    // Convert array to an abc-formatted string i.e. ["C"," ","c'2","|"] -> "C c'2|"
    return { abcNotes: abcNotes.join(''), pitches };
  };

  let { abcNotes, pitches } = buildAbcNotes(defaultLevel);
  if (props.clef === 'f' && pitches.some(p => note(p).midi < LOWEST_BASS_CLEF_MIDI)) {
    ({ abcNotes } = buildAbcNotes(defaultLevel + 1));
  }

  let nobars = (abcNotes.split("|").length - 1);
  let staffwidth = 740;
  if(props.group)
  {
    staffwidth = staffwidth/2; //props.group.members;
  }
  const abcnotation = `%%staffwidth ${staffwidth}\nX:1 \nL:${defaultNoteLength}\nK:C ${clef}\nT:${title}\n${abcNotes}`;
  return(

    <Notification color="white" style={{ padding:0,  marginTop: 10, marginBottom: 10, marginRight: 0}}>
{/*     <Notification color="white" style={{ padding:0,  marginTop: 10, marginBottom: 10, marginRight: 0 }}> */}
    <Abcjs
      abcNotation={abcnotation}
      parserParams={{}}
      engraverParams={{ responsive: 'resize' }}
      renderParams={{ viewportHorizontal: false }}
    /> 
  </Notification>


  )
}

/* function notesPropsAreEqual(prevNotes, nextNotes) {
  return prevNotes.t === nextNotes.t;
}

export default React.memo(Notes, notesPropsAreEqual); */
export default Notes;